import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconArrowRight from "../../../img/icons/arrow_right.svg";
import iconCircleClose from "../../../img/icons/circle-close.svg";
import { LoadingSpinner } from "../../common/LoadingSpinner";
import { ajax } from "../../utils/ajax";
import { focusElement } from "../../utils/keyboardFocus";

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

const initAccordions = (modalContentElem: HTMLDivElement | null): void => {
    if (modalContentElem) {
        Array.from(
            modalContentElem.querySelectorAll<HTMLElement>(".accordion"),
        ).forEach((elem) => {
            if (elem.classList.contains("accordion--loaded")) {
                return;
            }
            elem.classList.add("accordion--loaded");
            elem.addEventListener("click", function (event) {
                event.preventDefault();
                this.classList.toggle("accordion--is-active");
                if (!this.parentNode) {
                    return;
                }
                Array.from(this.parentNode.children)
                    .filter((e) => {
                        return e.classList.contains("accordion-target");
                    })
                    .forEach((e) => {
                        e.classList.toggle("accordion-target--is-active");
                    });
            });
        });
    }
};

const loadModalContent = (
    offerDetailURL: string,
    setModalContent: (content: string) => void,
): void => {
    void ajax
        .get(offerDetailURL)
        .then((resp) => {
            const document = getDocument();
            if (!document) {
                throw new Error("Document is not available");
            }
            const offerDetailHTML = document.createElement("html");
            offerDetailHTML.innerHTML = resp.text;
            const offerDetailContent =
                offerDetailHTML.querySelectorAll("#main-content > *");
            if (offerDetailContent.length <= 0) {
                throw new Error(
                    "Couldn't find any Offer Detail Content at this link",
                );
            }
            return offerDetailContent[0].outerHTML;
        })
        .then((result) => {
            setModalContent(result);
        });
};

const checkForAnchor = (
    anchorName: string,
    modalContentElem: HTMLDivElement | null,
): void => {
    if (anchorName.trim() !== "" && modalContentElem) {
        const anchorLink = modalContentElem.querySelector(
            '[id="' + anchorName + '"]',
        );
        if (anchorLink) {
            anchorLink.scrollIntoView({ behavior: "smooth" });
        }

        // close button is automatically focused when the modal first opens
        focusElement(".coffer-modal__close");
    }
};

const renderModalContent = (
    modalContent: string,
    modalContentElem: React.RefObject<HTMLDivElement | null>,
): React.ReactNode => {
    if (!modalContent) {
        return <LoadingSpinner />;
    }

    return (
        <div
            className="offer-modal__content"
            ref={modalContentElem}
            dangerouslySetInnerHTML={{ __html: modalContent }}
        ></div>
    );
};

const renderModalTrigger = (
    props: {
        offerDetailURL: string;
        ctaText?: string;
        ctaArrow?: boolean;
        isButton?: boolean;
    },
    onOpenModal: (event: React.MouseEvent<HTMLElement>) => void,
): React.ReactNode => {
    if (props.ctaArrow) {
        return (
            <button
                className="limited-time-offer-tile-block__arrow"
                onClick={onOpenModal}
            >
                <SVG
                    className="limited-time-offer-tile-block__arrow-icon"
                    src={iconArrowRight}
                    title={t`Right Arrow Icon`}
                />
            </button>
        );
    }
    const triggerClasses = classNames({
        "offer-modal__trigger": true,
        "button": props.isButton,
    });

    const triggerText = !props.ctaText
        ? t`See Details` + " <span>></span>"
        : props.ctaText;

    return (
        <button
            dangerouslySetInnerHTML={{ __html: triggerText }}
            className={triggerClasses}
            onClick={onOpenModal}
        ></button>
    );
};

export const OfferModal = (props: {
    offerDetailURL: string;
    ctaText?: string;
    ctaArrow?: boolean;
    isButton?: boolean;
}): React.ReactNode => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [windowWidth, setWindowWidth] = React.useState(() => {
        const window = getWindow();
        return window?.innerWidth ?? 0;
    });
    const [modalContent, setModalContent] = React.useState("");
    const [anchorName, setAnchorName] = React.useState("");

    const modalContentElem = React.useRef<HTMLDivElement | null>(null);
    const modalTriggerElem = React.useRef<HTMLElement | null>(null);
    const hasMountedRef = React.useRef(false);

    const onOpenModal = (event: React.MouseEvent<HTMLElement>): void => {
        let anchor = "";
        modalTriggerElem.current = event.currentTarget as HTMLElement;
        const eventTarget = event.target as HTMLElement;
        const sup = modalTriggerElem.current.getElementsByTagName("sup");
        if (sup.length > 0) {
            if (eventTarget.tagName === "SUP") {
                anchor = sup[0].innerText;
            }
        }

        setTimeout(() => {
            setModalIsOpen(true);
            setAnchorName(anchor);
        }, 100);
    };

    const onCloseModal = (): void => {
        setModalIsOpen(false);
    };

    const handleCheckForAnchor = (): void => {
        checkForAnchor(anchorName, modalContentElem.current);
    };

    // Load modal content whenever the source URL changes
    React.useEffect(() => {
        loadModalContent(props.offerDetailURL, setModalContent);
    }, [props.offerDetailURL]);

    // Setup resize listener/accordions once on mount (componentDidMount)
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        const handleResize = (): void => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        initAccordions(modalContentElem.current);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Handle updates after each render (componentDidUpdate equivalent)
    React.useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        // Full content of the modal does not always display and sometimes appears blank on iPad
        // This issue occurs on only Safari in iPad.
        // This hack fixes the issue - Wait 0.2 sec for the content of the modal rendered and then set overflow-y: scroll
        const timeoutId = setTimeout(() => {
            const document = getDocument();
            if (!document) {
                return;
            }
            const offerDetails = document.querySelector(
                ".offer-modal__content > *",
            );
            const activeClass = "offer-details-page--active";
            if (offerDetails) {
                if (offerDetails.classList.contains(activeClass)) {
                    offerDetails.classList.remove(activeClass);
                }
                offerDetails.classList.add(activeClass);
            }

            initAccordions(modalContentElem.current);
        }, 200);

        return () => {
            clearTimeout(timeoutId);
        };
    });

    // Check for anchor after modal content is loaded
    React.useEffect(() => {
        if (modalContent) {
            handleCheckForAnchor();
        }
    }, [modalContent]);

    let modalWidth = "";
    let modalHeight = "";
    let modalPadding = "";
    let borderRadius = "20px";
    let closeTimeout = 0;

    if (windowWidth < 768) {
        modalWidth = "100%";
        modalHeight = "100%";
        modalPadding = "32px 16px";
        borderRadius = "0";
    } else {
        modalWidth = "70%";
        modalHeight = "85vh";
        modalPadding = "32px";
    }

    if (windowWidth < 500) {
        closeTimeout = 500;
    }

    const modalStyle = {
        content: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            margin: "auto",
            width: modalWidth,
            height: modalHeight,
            padding: modalPadding,
            boxShadow: "none",
            borderRadius: borderRadius,
            overflow: "hidden",
        },
    };

    return (
        <div>
            {renderModalTrigger(props, onOpenModal)}
            <Modal
                aria={{ modal: true }}
                className={{
                    base: "offer-modal",
                    afterOpen: "offer-modal--after-open",
                    beforeClose: "offer-modal--before-close",
                }}
                overlayClassName={{
                    base: "offer-modal__overlay",
                    afterOpen: "offer-modal__overlay--after-open",
                    beforeClose: "offer-modal__overlay--before-close",
                }}
                contentLabel={t`Offers`}
                closeTimeoutMS={closeTimeout}
                style={modalStyle}
                isOpen={modalIsOpen}
                onAfterOpen={handleCheckForAnchor}
                onRequestClose={onCloseModal}
                role="dialog"
            >
                <button
                    aria-label={t`close`}
                    className="offer-modal__close"
                    onClick={onCloseModal}
                >
                    <SVG
                        aria-hidden="true"
                        className="offer-modal__close-icon"
                        src={iconCircleClose}
                        title={t`Close Icon`}
                    />
                </button>
                {renderModalContent(modalContent, modalContentElem)}
            </Modal>
        </div>
    );
};
